import { useQuery } from '@tanstack/react-query';
import type { ReactElement, ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '@/app/auth/context';
import { PageHeader, WidgetCard } from '@/components/admin/AdminShell';
import { CountBadge } from '@/components/admin/CountBadge';
import { ErrorState, SkeletonRows } from '@/components/admin/states';
import { ROLE_LABELS } from '@/features/auth/api';
import {
  fetchDashboard,
  type DashboardSummary,
  type NextEvent,
  type OperationItem,
  type PendingEventAction,
  type WeekMeeting,
} from '@/features/dashboard/api';
import { surveyTabId } from '@/features/events/eventSurveyTabs';
import type { EventSurveyKind } from '@/features/surveys/api';

/**
 * ロール別ホーム（指示書 §10 / §7-4: WPダッシュボード風ウィジェット / §12 M12）。
 * データは GET /dashboard が1回で返す（対象外ウィジェットは null）。
 * 表示順は §13 仮決め7 の設定値（widget_order）に従う。
 */
export function DashboardPage(): ReactElement {
  const { user } = useAuth();
  const query = useQuery({ queryKey: ['dashboard'], queryFn: fetchDashboard, enabled: !!user });

  if (!user) return <></>;

  const role = user.active_role;

  return (
    <>
      <PageHeader title="ダッシュボード" description={`${ROLE_LABELS[role]}としてログイン中`} />
      {query.isPending ? (
        <SkeletonRows rows={4} columns={3} />
      ) : query.isError ? (
        <ErrorState onRetry={() => void query.refetch()} />
      ) : role === 'super_admin' ? (
        <SuperAdminHome />
      ) : (
        <RoleHome data={query.data} />
      )}
    </>
  );
}

function RoleHome({ data }: { data: DashboardSummary }): ReactElement {
  const { user } = useAuth();
  const isSuccess = user?.active_role === 'success';
  // 事務局は上の「次のイベント」「今日の対応」に集約済み（D-108）。
  // 同じ内容をカードでも出すと二重に見えるため、下のカードからは外す（D-124）
  const hasSummaryPanels = Boolean(data.next_event || data.operations);
  const widgets: Record<string, ReactNode> = {
    ...(hasSummaryPanels ? {} : { events: <EventsWidget key="events" data={data} /> }),
    pending: <PendingWidgets key="pending" data={data} />,
    messages: <MessagesWidget key="messages" data={data} />,
  };
  const order = data.widget_order.filter((key) => key in widgets);
  const rest = Object.keys(widgets).filter((key) => !order.includes(key));

  const actionQueueTitle = data.operations
    ? '欠席申請の承認待ち'
    : isSuccess
      ? '日程調整が必要な面談'
      : '次にやること';

  return (
    <>
      {/* 事務局は「今日やること」から入る（D-108）。他ロールでは API が null を返す */}
      {data.pending_event_actions && (
        <ActionQueue
          title={actionQueueTitle}
          emptyText="対応が必要な作業はありません。"
          hideWhenEmpty={Boolean(data.operations)}
          items={data.pending_event_actions}
          badgeLabel={isSuccess ? '日程調整' : '未対応'}
        />
      )}
      {data.next_event && <NextEventPanel event={data.next_event} />}
      {data.operations && <OperationsQueue items={data.operations} />}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {[...order, ...rest].map((key) => widgets[key])}
      </div>
    </>
  );
}

const URGENCY_LABELS: Record<string, string> = {
  overdue: '期限超過',
  today: '今日',
  soon: '近日',
};

/**
 * 受講生の「次にやること」（D-231）／事務局の欠席申請承認待ち（D-232）。
 */
function ActionQueue({
  title,
  emptyText,
  hideWhenEmpty,
  items,
  badgeLabel = '未対応',
}: {
  title: string;
  emptyText: string;
  hideWhenEmpty?: boolean;
  items: PendingEventAction[];
  badgeLabel?: string;
}): ReactElement | null {
  if (items.length === 0) {
    if (hideWhenEmpty) return null;
    return (
      <section className="mb-4 rounded-sm border border-wpborder bg-white px-4 py-3">
        <h2 className="mb-1 font-semibold text-wptext-strong">{title}</h2>
        <p className="text-wptext-muted">{emptyText}</p>
      </section>
    );
  }

  return (
    <section className="mb-4 rounded-sm border border-wpnotice-warning bg-wpnotice-warning/10 px-4 py-3">
      <h2 className="mb-1 inline-flex items-center gap-1.5 font-semibold text-wptext-strong">
        {title}
        <CountBadge count={items.length} label={badgeLabel} />
        <span className="font-normal text-wptext-muted">
          {badgeLabel} {items.length}件
        </span>
      </h2>
      <ul className="divide-y divide-wpborder">
        {items.map((item, index) => (
          <li key={`${item.event_id}-${item.kind}-${item.tab}-${index}`} className="py-2">
            <Link
              to={`/events/${item.event_id}?tab=${item.tab}`}
              className="font-semibold text-wpaccent hover:underline"
            >
              {item.label}
            </Link>
            <span className="ml-2 text-wptext-muted">{item.event_title}</span>
            {item.detail && <p className="mt-1 text-wptext-muted">{item.detail}</p>}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * 事務局の「今日の対応」（D-108）。
 * 未対応の業務を1か所に集め、締切の近い順に並べる。押すと担当の画面へ移動する。
 */
function OperationsQueue({ items }: { items: OperationItem[] }): ReactElement {
  const total = items.reduce((sum, item) => sum + item.count, 0);

  if (items.length === 0) {
    return (
      <section className="mb-4 rounded-sm border border-wpborder bg-white px-4 py-3">
        <h2 className="mb-1 font-semibold text-wptext-strong">今日の対応</h2>
        <p className="text-wptext-muted">未対応の作業はありません。</p>
      </section>
    );
  }

  return (
    <section className="mb-4 rounded-sm border border-wpborder bg-white px-4 py-3">
      <h2 className="mb-1 inline-flex items-center gap-1.5 font-semibold text-wptext-strong">
        今日の対応
        <CountBadge count={total} label="未対応" />
        <span className="font-normal text-wptext-muted">
          未対応 {total}件（急ぐものから並べています）
        </span>
      </h2>
      <ul className="divide-y divide-wpborder">
        {items.map((item) => (
          <li key={item.key} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 py-2">
            <span
              className={`shrink-0 rounded-sm px-2 py-0.5 text-xs font-semibold ${
                item.urgency === 'overdue'
                  ? 'bg-wpnotice-error text-white'
                  : item.urgency === 'today'
                    ? 'bg-wpnotice-warning text-wptext-strong'
                    : 'bg-wpborder text-wptext-strong'
              }`}
            >
              {URGENCY_LABELS[item.urgency] ?? item.urgency}
            </span>
            <Link
              to={item.link === '/invitations' ? '/members' : item.link}
              className="inline-flex items-center gap-1.5 font-semibold text-wpaccent hover:underline"
            >
              {item.label}
              <CountBadge count={item.count} label="未対応" />
            </Link>
            <span className="text-wptext-muted">{item.description}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** 次回イベントと、その進行段階（D-108）。今どこまで済んだかを1本の帯で示す。 */
function NextEventPanel({ event }: { event: NextEvent }): ReactElement {
  const unanswered = event.target_count - event.answered_count;
  return (
    <section className="mb-4 rounded-sm border border-wpborder bg-white px-4 py-3">
      <h2 className="mb-1 inline-flex items-center gap-1.5 font-semibold text-wptext-strong">
        次のイベント
        <span className="font-normal text-wptext-muted">
          {event.days_left <= 0 ? '本日' : `あと${event.days_left}日`}
        </span>
      </h2>
      <p className="mb-2">
        <Link to={`/events/${event.id}`} className="text-wpaccent hover:underline">
          {event.title}
        </Link>
        <span className="ml-2 text-wptext-muted">
          {new Date(event.starts_at).toLocaleString('ja-JP', {
            month: 'numeric',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
          {event.venue ? ` / ${event.venue}` : ''}
        </span>
      </p>
      {event.target_count > 0 && (
        <p className="mb-2 inline-flex items-center gap-1.5 text-wptext-muted">
          出欠の回答 {event.answered_count} / {event.target_count}名
          <CountBadge count={unanswered} label="未回答" />
        </p>
      )}
      <ol className="flex flex-wrap gap-x-4 gap-y-1">
        {event.milestones.map((milestone) => (
          <li key={milestone.key} className="flex items-center gap-1.5">
            <span
              aria-hidden="true"
              className={`flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-semibold ${
                milestone.state === 'done'
                  ? 'bg-wpnotice-success text-white'
                  : milestone.state === 'current'
                    ? 'bg-wpaccent text-white'
                    : 'bg-wpborder text-wptext-strong'
              }`}
            >
              {milestone.state === 'done' ? '✓' : ''}
            </span>
            <span
              className={
                milestone.state === 'current'
                  ? 'font-semibold text-wptext-strong'
                  : 'text-wptext-muted'
              }
            >
              {milestone.label}
            </span>
            {milestone.state === 'current' && (
              <span className="rounded-sm bg-wpaccent px-1.5 py-0.5 text-[10px] font-semibold text-white">
                いまここ
              </span>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
}

function EventsWidget({ data }: { data: DashboardSummary }): ReactElement {
  const { user } = useAuth();
  const scheduleBadgeLabel = user?.active_role === 'success' ? '日程調整' : '未対応';
  const todoByEvent = new Map<string, number>();
  for (const item of data.pending_event_actions ?? []) {
    todoByEvent.set(item.event_id, (todoByEvent.get(item.event_id) ?? 0) + 1);
  }
  return (
    <WidgetCard title="直近のイベント">
      {data.upcoming_events.length === 0 ? (
        <p className="text-wptext-muted">直近のイベントはありません。</p>
      ) : (
        <ul className="space-y-2">
          {data.upcoming_events.map((event) => (
            <li key={event.id} className="flex flex-wrap items-center gap-2">
              <Link to={`/events/${event.id}`} className="text-wpaccent hover:underline">
                {event.title}
              </Link>
              <CountBadge
                count={todoByEvent.get(event.id) ?? 0}
                label={scheduleBadgeLabel}
                showCount={false}
              />
              <span className="text-wptext-muted">
                {event.starts_at
                  ? new Date(event.starts_at).toLocaleString('ja-JP', {
                      month: 'numeric',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : '日程調整中'}
                {event.venue ? ` / ${event.venue}` : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
    </WidgetCard>
  );
}

/** 「未提出・対応待ち」の中身はロールで変わる（対象外の値は API が null を返す）. */
function PendingWidgets({ data }: { data: DashboardSummary }): ReactElement {
  return (
    <>
      {data.pending_surveys !== null && data.pending_event_actions === null && (
        <WidgetCard title="未回答の課題・アンケート">
          {data.pending_surveys.length === 0 ? (
            <p className="text-wptext-muted">未回答の課題・アンケートはありません。</p>
          ) : (
            <ul className="space-y-2">
              {data.pending_surveys.map((survey) => (
                <li key={survey.event_survey_id}>
                  <Link
                    to={`/events/${survey.event_id}?tab=${surveyTabId(
                      survey.event_type_code,
                      survey.template_kind as EventSurveyKind,
                    )}`}
                    className="text-wpaccent hover:underline"
                  >
                    {survey.survey_name}
                  </Link>
                  <span className="ml-2 text-wptext-muted">
                    {survey.event_title}
                    {survey.deadline_at
                      ? ` / 期限 ${new Date(survey.deadline_at).toLocaleDateString('ja-JP')}`
                      : ''}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </WidgetCard>
      )}
      {data.assigned_member_count !== null && (
        // サクセス担当のメニューから会員一覧は外した（D-182）ため、導線はイベント一覧にする
        <CountWidget title="担当会員" count={data.assigned_member_count} unit="名" to="/events" />
      )}
      {data.open_inbox_count !== null && data.operations === null && (
        <CountWidget
          title="共有受信箱の未対応件数"
          count={data.open_inbox_count}
          unit="件"
          to="/inbox"
          emptyText="未対応の問い合わせはありません。"
        />
      )}
      {data.pending_evaluation_count !== null && (
        <CountWidget
          title="優秀者選定・態度FBが未入力のイベント"
          count={data.pending_evaluation_count}
          unit="件"
          to="/events"
          emptyText="未入力のイベントはありません。"
        />
      )}
      {data.week_meetings !== null && <WeekMeetingsWidget meetings={data.week_meetings} />}
    </>
  );
}

/** サクセス担当の「今週の面談」（日時が確定した面談を時刻順に / D-110 A-7・D-182）. */
function WeekMeetingsWidget({ meetings }: { meetings: WeekMeeting[] }): ReactElement {
  return (
    <WidgetCard title="今週の面談（確定済み）">
      {meetings.length === 0 ? (
        <p className="text-wptext-muted">今週の確定済みの面談はありません。</p>
      ) : (
        <ul className="space-y-2">
          {meetings.map((meeting) => (
            <li key={meeting.event_id} className="flex flex-wrap items-baseline gap-x-2">
              <span className="whitespace-nowrap tabular-nums text-wptext-muted">
                {new Date(meeting.starts_at).toLocaleString('ja-JP', {
                  month: 'numeric',
                  day: 'numeric',
                  weekday: 'short',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
              <Link to={`/events/${meeting.event_id}`} className="text-wpaccent hover:underline">
                {meeting.member_name}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </WidgetCard>
  );
}

function MessagesWidget({ data }: { data: DashboardSummary }): ReactElement {
  return (
    <CountWidget
      title="新着メッセージ"
      count={data.unread_messages}
      unit="件"
      to="/messages"
      emptyText="新着メッセージはありません。"
    />
  );
}

function CountWidget({
  title,
  count,
  unit,
  to,
  emptyText,
}: {
  title: string;
  count: number;
  unit: string;
  to: string;
  emptyText?: string;
}): ReactElement {
  return (
    <WidgetCard title={title}>
      {count === 0 && emptyText ? (
        <p className="text-wptext-muted">{emptyText}</p>
      ) : (
        <Link to={to} className="text-2xl font-semibold text-wpaccent hover:underline">
          {count}
          {unit}
        </Link>
      )}
    </WidgetCard>
  );
}

/** 運営ダッシュボード（D-046: 独自コンテンツは作らず、最小6画面への導線のみ）. */
function SuperAdminHome(): ReactElement {
  const links = [
    { to: '/superadmin/office-admins', label: '事務局管理者（緊急復旧・TOTPリセット）' },
    { to: '/superadmin/integrations', label: '外部連携（非常時の確認用）' },
    { to: '/superadmin/jobs', label: 'ジョブ・障害対応' },
    { to: '/audit-logs', label: 'システム監査ログ' },
  ];
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {links.map((link) => (
        <WidgetCard key={link.to} title={link.label}>
          <Link to={link.to} className="text-wpaccent hover:underline">
            開く →
          </Link>
        </WidgetCard>
      ))}
    </div>
  );
}
